import { useState, useEffect } from "react";
import Modal from "../modal/Modal";
import { MODAL_TYPE, MODAL_HEADER_STYLE } from "../../constants";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import useEventBus from "../../hooks/useEventBus";
import { RECOMMENDED_OUTFITS_OPEN_MODAL_EVENT } from "./constants";
import { ProductContextProvider } from "../../context/product/ProductContext";
import { VariantContextProvider } from "../../context/variant/VariantContext";
import RecommendedOutfitsProduct from "./RecommendedOutfitsProduct";
import { fireRecommendedOutfitsModalOpen } from "../../analytics/recommendedOutfits";
import { useRecommendedOutfits } from "../../context/recommendedOutfits/RecommendedOutfitsContext";
import { useStockPrice } from "../../context/stockPrice/StockPriceContext";
import { getSummariesAndStockData } from "./utils";
import getWindow from "../../utils/window";

export default function RecommendedOutfitsModal() {
  const [modalId] = useState(
    `${MODAL_TYPE.SIDE}${Math.floor(Math.random() * 10000)}`,
  );
  const [open, setOpen] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [focusOnCloseElement, setFocusOnCloseElement] = useState(null);
  const [
    enhancedRecommendedOutfitsProducts,
    setEnhancedRecommendedOutfitsProducts,
  ] = useState(null);
  const [
    enhancedRecommendedOutfitsProductsError,
    setEnhancedRecommendedOutfitsProductsError,
  ] = useState(false);

  const { getTranslation } = useRuntimeConstants();
  const { recommendedOutfits } = useRecommendedOutfits();
  const { products: heroProductWithStockprice } = useStockPrice();

  const getEnhancedRecommendedOutfits = async (recommendedOutfits) => {
    try {
      const recommendedOutfitsProductDetails = await getSummariesAndStockData({
        recommendedOutfits,
      });
      if (
        !recommendedOutfitsProductDetails?.length ||
        !heroProductWithStockprice
      ) {
        setEnhancedRecommendedOutfitsProductsError(true);
        return [];
      }
      return [
        {
          ...heroProductWithStockprice,
          isHeroProduct: true,
        },
        ...recommendedOutfitsProductDetails,
      ];
    } catch {
      setEnhancedRecommendedOutfitsProductsError(true);
      return [];
    }
  };

  useEffect(() => {
    if (enhancedRecommendedOutfitsProductsError) {
      getWindow().asos.eventBus.emit("notificationBar", {
        type: "error",
        copy: getTranslation("pdp_status_error_technical_difficulties"),
        refocusId: "recommendedOutfitsSection",
      });
    }
  }, [enhancedRecommendedOutfitsProductsError, getTranslation]);

  useEventBus(
    RECOMMENDED_OUTFITS_OPEN_MODAL_EVENT,
    async ({ focusOnCloseElement }) => {
      setEnhancedRecommendedOutfitsProductsError(false);
      const enhancedRecommendedOutfitsProductsData =
        await getEnhancedRecommendedOutfits(recommendedOutfits);

      if (enhancedRecommendedOutfitsProductsData.length) {
        setEnhancedRecommendedOutfitsProducts(
          enhancedRecommendedOutfitsProductsData,
        );
        setOpen(true);
        setContentLoaded(true);
        setFocusOnCloseElement(focusOnCloseElement);
      }
    },
    [recommendedOutfits, heroProductWithStockprice],
  );

  useEffect(() => {
    fireRecommendedOutfitsModalOpen({
      products: enhancedRecommendedOutfitsProducts,
    });
  }, [enhancedRecommendedOutfitsProducts]);

  if (
    !recommendedOutfits?.length ||
    !Object.keys(heroProductWithStockprice || {}).length
  ) {
    return null;
  }

  const modalControls = {
    closeModal: () => setOpen(false),
    setFocusOnCloseElement,
  };

  const productsAvailable = enhancedRecommendedOutfitsProducts?.length;
  const subtitle = productsAvailable
    ? getTranslation("generic_item_count", {
        smart_count: productsAvailable,
        count: productsAvailable,
      })
    : "";

  return (
    <Modal
      title={getTranslation("pdp_styled_for_you")}
      subtitle={subtitle}
      modalId={modalId}
      modalType={MODAL_TYPE.SIDE}
      modalHeaderStyle={MODAL_HEADER_STYLE.DARK}
      closeModal={() => setOpen(false)}
      contentLoaded={contentLoaded}
      open={open}
      focusOnClose={focusOnCloseElement}
    >
      {enhancedRecommendedOutfitsProducts?.map(
        (recommendedOutfitProduct, index) => {
          return (
            <ProductContextProvider
              key={recommendedOutfitProduct.id}
              products={enhancedRecommendedOutfitsProducts}
              productId={recommendedOutfitProduct.id}
            >
              <VariantContextProvider>
                <RecommendedOutfitsProduct
                  product={recommendedOutfitProduct}
                  key={recommendedOutfitProduct.id}
                  modalControls={modalControls}
                  index={index}
                  modalOpen={open}
                />
              </VariantContextProvider>
            </ProductContextProvider>
          );
        },
      )}
    </Modal>
  );
}
